import { useCallback, useEffect, useState } from 'react'
import { ToastContainer, toast } from 'react-toastify'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { Empty, Flex, Spin } from 'antd'
import * as XLSX from 'xlsx'
import {
    campaignGetAll,
    campaignGetById,
    campaignSend,
    campaignSendVoice,
    campaignUpload,
} from '../services/Api'

const STATUS_COLORS = {
    pending: 'bg-amber-100 text-amber-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    sent: 'bg-green-100 text-green-800',
    delivered: 'bg-emerald-100 text-emerald-800',
    initiated: 'bg-sky-100 text-sky-800',
    ringing: 'bg-indigo-100 text-indigo-800',
    answered: 'bg-teal-100 text-teal-800',
    'no-answer': 'bg-orange-100 text-orange-800',
    busy: 'bg-orange-100 text-orange-800',
}

const SMSCampaignManagement = () => {
    const [campaigns, setCampaigns] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [sending, setSending] = useState(null)
    const [sendingVoice, setSendingVoice] = useState(null)
    const [openDetail, setOpenDetail] = useState(false)
    const [selectedCampaign, setSelectedCampaign] = useState(null)
    const [campaignUsers, setCampaignUsers] = useState([])
    const [formValues, setFormValues] = useState({
        title: '',
        message_template: 'Hi {firstName} {lastName}, welcome to UrbanCab!',
        file: null,
        // message_template_id: '',
        // user_type: 'new_user'
    })

    const getAllCampaigns = useCallback(() => {
        setIsLoading(true)
        campaignGetAll()
            .then((res) => {
                setCampaigns(res?.data?.data || res?.data || [])
                setIsLoading(false)
            })
            .catch((err) => {
                setIsLoading(false)
                toast(err?.response?.data?.message || 'Failed to fetch campaigns.')
            })
    }, [])

    useEffect(() => {
        getAllCampaigns()
    }, [getAllCampaigns])

    const handleChange = (field, value) => {
        setFormValues((prev) => ({ ...prev, [field]: value }))
    }

    const handleFileChange = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        const ext = file.name?.split('.').pop()?.toLowerCase()
        if (ext !== 'xlsx' && ext !== 'xls') {
            toast('Please upload an XLSX or XLS file.')
            return
        }
        setFormValues((prev) => ({ ...prev, file }))
    }

    const handleDownloadTemplate = () => {
        const templateData = [
            { firstName: 'John', lastName: 'Doe', phoneNumber: '9876543210' },
            { firstName: 'Jane', lastName: 'Smith', phoneNumber: '9123456789' },
        ]
        const ws = XLSX.utils.json_to_sheet(templateData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Users')
        XLSX.writeFile(wb, 'campaign_template.xlsx')
    }

    const handleUpload = () => {
        const { title, message_template, file,
            //  message_template_id, user_type 
        } = formValues
        if (!title?.trim()) {
            toast('Campaign title is required.')
            return
        }
        if (!message_template?.trim()) {
            toast('Message template is required.')
            return
        }
        // if (!message_template_id?.trim()) {
        //     toast('Message template ID is required.')
        //     return
        // }
        if (!message_template.includes('{firstName}')) {
            toast('Message template must contain {firstName} placeholder.')
            return
        }
        // if (user_type === 'new_user' && !file) {
        //     toast('Please select an XLSX file.')
        //     return
        // }

        const formData = new FormData()
        formData.append('title', title.trim())
        formData.append('message_template', message_template.trim())
        // formData.append('message_template_id', message_template_id.trim())
        // if (user_type === 'new_user') {
        formData.append('file', file)
        // }
        // formData.append('user_type', user_type)
        setUploading(true)
        campaignUpload(formData)
            .then((res) => {
                toast(res?.data?.message || 'Campaign created successfully.')
                setFormValues({ title: '', message_template: 'Hi {name}, welcome to UrbanCab!', message_template_id: '', file: null, user_type: 'new_user' })
                getAllCampaigns()
                setUploading(false)
            })
            .catch((err) => {
                console.log(err, '-------==============>');
                toast(err?.response?.data?.message || 'Failed to create campaign.')
                setUploading(false)
            })
    }

    const handleSendCampaign = (campaign) => {
        // if (campaign?.status === 'processing') {
        //     toast('Campaign is already sending.')
        //     return
        // }
        if (campaign?.status === 'completed') {
            toast('Campaign has already been completed.')
            return
        }
        setSending(campaign._id)
        campaignSend(campaign._id)
            .then((res) => {
                toast(res?.data?.message || 'Campaign send triggered.')
                getAllCampaigns()
                setSending(null)
            })
            .catch((err) => {
                toast(err?.response?.data?.message || 'Failed to send campaign.')
                setSending(null)
            })
    }

    const handleSendVoiceCampaign = (campaign) => {
        if (campaign?.status === 'completed') {
            toast('Campaign has already been completed.')
            return
        }
        setSendingVoice(campaign._id)
        campaignSendVoice(campaign._id)
            .then((res) => {
                toast(res?.data?.message || 'Voice campaign triggered.')
                getAllCampaigns()
                setSendingVoice(null)
            })
            .catch((err) => {
                toast(err?.response?.data?.message || 'Failed to trigger voice campaign.')
                setSendingVoice(null)
            })
    }

    const handleOpenDetail = (campaign) => {
        setSelectedCampaign(campaign)
        setOpenDetail(true)
        campaignGetById(campaign._id)
            .then((res) => {
                const data = res?.data?.data || res?.data
                setCampaignUsers(data?.users || data?.campaignUsers || [])
            })
            .catch(() => {
                setCampaignUsers([])
                toast('Failed to load campaign users.')
            })
    }

    const handleCloseDetail = () => {
        setOpenDetail(false)
        setSelectedCampaign(null)
        setCampaignUsers([])
    }

    return (
        <>
            <ToastContainer />
            <div className="space-y-6 px-5 py-5">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    <h1 className="text-2xl font-semibold tracking-tight text-[#3A4352]">
                        Bulk SMS Campaign
                    </h1>
                    <button
                        type="button"
                        className="flex items-center gap-2 border border-[#5E6977] text-[#5E6977] text-sm py-1.5 px-4 rounded-md hover:bg-gray-50 active:scale-95"
                        onClick={handleDownloadTemplate}
                    >
                        Download XLS Template
                    </button>
                </div>

                {/* Create Campaign Section */}
                <div className="border-2 border-gray-400 rounded-md p-5">
                    <p className="text-base font-medium text-[#3A4352] mb-4">Create New Campaign</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col">
                            <label className="text-gray-400 text-sm font-medium">Campaign Title</label>
                            <input
                                className="outline-none ms-2 mt-1 border border-gray-400 h-9 ps-2 rounded-md w-[95%] sm:w-[80%] placeholder:text-sm"
                                placeholder="e.g. Welcome Campaign"
                                type="text"
                                value={formValues.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-gray-400 text-sm font-medium">Message Template</label>
                            <textarea
                                className="outline-none ms-2 mt-1 border border-gray-400 h-[100px] ps-2 rounded-md w-[95%] sm:w-[80%] placeholder:text-sm"
                                placeholder="Hi, ..."
                                type="text"
                                value={formValues.message_template}
                                onChange={(e) => handleChange('message_template', e.target.value)}
                            />
                        </div>
                        {/* <div className="flex flex-col">
                            <label className="text-gray-400 text-sm font-medium">Message Template ID</label>
                            <input
                                className="outline-none ms-2 mt-1 border border-gray-400 h-9 ps-2 rounded-md w-[95%] sm:w-[80%] placeholder:text-sm"
                                placeholder="Enter Message Template ID"
                                type="text"
                                value={formValues.message_template_id}
                                onChange={(e) => handleChange('message_template_id', e.target.value)}
                            />
                        </div> */}
                        {/* Dropdown for User Type */}
                        {/* <div className="flex flex-col">
                            <label className="text-gray-400 text-sm font-medium">User Type</label>
                            <select
                                className="outline-none ms-2 mt-1 border border-gray-400 h-9 ps-2 rounded-md w-[95%] sm:w-[80%] placeholder:text-sm"
                                value={formValues.user_type}
                                onChange={(e) => handleChange('user_type', e.target.value)}
                            >
                                <option value="new_user">New Users</option>
                                <option value="verified_users">Verified Users</option>
                                <option value="unverified_users">Unverified Users</option>
                                <option value="all_existing_user">All Existing User</option>
                            </select>
                        </div> */}
                        {/* {formValues.user_type === 'new_user' &&  */}
                        <div className="flex flex-col sm:col-span-2">
                            <label className="text-gray-400 text-sm font-medium">XLSX File (columns: name, phone)</label>
                            <div className="flex items-center gap-2 ms-2 mt-1 flex-wrap">
                                <div className="flex-1 min-w-[140px] flex items-center h-9 rounded-md border border-gray-400 bg-[#FBFBFB] ps-2 pe-1">
                                    <span className="truncate text-sm text-gray-600">
                                        {formValues.file?.name || 'Choose XLSX file'}
                                    </span>
                                </div>
                                <div className="flex gap-2">

                                    <label className="flex items-center gap-2 bg-black text-white text-sm py-1.5 px-4 rounded-md cursor-pointer active:scale-95">
                                        Upload File
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>
                        {/* } */}
                    </div>
                    <div className="flex justify-end mt-4">
                        <button
                            className="bg-[#008400] text-white px-5 py-2 rounded-md active:scale-95 duration-500 disabled:opacity-60"
                            onClick={handleUpload}
                            disabled={uploading}
                        >
                            {uploading ? 'Creating...' : 'Create Campaign'}
                        </button>
                    </div>
                </div>

                {/* Campaigns Table */}
                <div>
                    <p className="text-base font-medium text-[#3A4352] mb-3">Campaigns</p>
                    {isLoading ? (
                        <div className="mt-10 flex justify-center items-center">
                            <Flex gap="middle" vertical>
                                <Spin tip="Loading" size="large">
                                    <div className="p-12 rounded-xl" />
                                </Spin>
                            </Flex>
                        </div>
                    ) : campaigns.length > 0 ? (
                        <div className="border rounded-lg overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-muted/50 bg-[#FFF0F2]">
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2 rounded-s-lg">S.No</th>
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Title</th>
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Total</th>
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Sent</th>
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Failed</th>
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Status</th>
                                        <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold rounded-e-lg">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map((campaign, index) => (
                                        <tr key={campaign._id || index} className="border-b last:border-0">
                                            <td className="py-3 px-4 text-sm text-[#62ABF4] text-center">{index + 1}</td>
                                            <td className="py-3 px-4 text-sm text-[#394253] text-center">{campaign.title}</td>
                                            <td className="py-3 px-4 text-sm text-[#394253] text-center">{campaign.total_users ?? 0}</td>
                                            <td className="py-3 px-4 text-sm text-[#394253] text-center">{campaign.sent_count ?? 0}</td>
                                            <td className="py-3 px-4 text-sm text-[#394253] text-center">{campaign.failed_count ?? 0}</td>
                                            <td className="py-3 px-4 text-center">
                                                <span
                                                    className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[campaign.status] || 'bg-gray-100 text-gray-800'
                                                        }`}
                                                >
                                                    {campaign.status || 'pending'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 flex justify-center gap-2">
                                                <button
                                                    className="border border-[#5E6977] text-[#5E6977] py-1 px-3 rounded-md text-sm"
                                                    onClick={() => handleOpenDetail(campaign)}
                                                >
                                                    View
                                                </button>
                                                {campaign.status !== 'completed' && (
                                                    <>
                                                        {/* <button
                                                            className="border border-[#008400] text-[#008400] py-1 px-3 rounded-md text-sm disabled:opacity-50"
                                                            onClick={() => handleSendCampaign(campaign)}
                                                            disabled={sending === campaign._id}
                                                        >
                                                            {sending === campaign._id ? 'Sending...' : 'Send SMS'}
                                                        </button> */}
                                                        <button
                                                            className="border border-[#5E6977] text-[#5E6977] py-1 px-3 rounded-md text-sm disabled:opacity-50"
                                                            onClick={() => handleSendVoiceCampaign(campaign)}
                                                            disabled={sendingVoice === campaign._id}
                                                        >
                                                            {sendingVoice === campaign._id ? 'Calling...' : 'Send Voice'}
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <Empty description="No campaigns yet. Create one above." />
                    )}
                </div>
            </div>

            {/* Campaign Detail Modal */}
            <Dialog open={openDetail} onClose={handleCloseDetail} className="relative">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200"
                />
                <div className="fixed inset-0 z-40 w-full overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <DialogPanel className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-white shadow-xl">
                            <div className="p-6">
                                <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                                    {selectedCampaign?.title} — Recipients
                                </DialogTitle>
                                <div className="mt-4 overflow-x-auto max-h-[70vh]">
                                    <table className="w-full text-sm">
                                        <thead className="bg-[#FFF0F2] sticky top-0">
                                            <tr>
                                                <th className="text-left py-2 px-3">S.No</th>
                                                <th className="text-left py-2 px-3">Name</th>
                                                <th className="text-left py-2 px-3">Phone</th>
                                                <th className="text-left py-2 px-3">Status</th>
                                                <th className="text-left py-2 px-3">Call duration</th>
                                                <th className="text-left py-2 px-3">Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaignUsers.map((u, i) => (
                                                <tr key={u._id || i} className="border-b">
                                                    <td className="py-2 px-3">{i + 1}</td>
                                                    <td className="py-2 px-3">{u.name}</td>
                                                    <td className="py-2 px-3">{u.phone}</td>
                                                    <td className="py-2 px-3">
                                                        <span
                                                            className={`inline-block px-2 py-0.5 rounded text-xs ${STATUS_COLORS[u.status] || 'bg-gray-100'
                                                                }`}
                                                        >
                                                            {u.twilio_status || u.status || 'pending'}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-3 text-xs text-gray-600">
                                                        {u.call_duration != null ? `${u.call_duration}s` : '-'}
                                                    </td>
                                                    <td className="py-2 px-3 text-red-600 text-xs max-w-[200px] truncate">
                                                        {u.error_message || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-6 py-3 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleCloseDetail}
                                    className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    Close
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default SMSCampaignManagement
